import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { projectsApi } from '@/lib/api';
import type { Project, ProjectDashboard, TaskStatus } from '@/types';

export function useProjects(includeArchived = false) {
  return useQuery({
    queryKey: ['projects', includeArchived],
    queryFn: async () => {
      const response = await projectsApi.getAll(
        includeArchived ? { includeArchived: 'true' } : undefined
      );
      return response.data as Project[];
    },
  });
}

export function useProject(projectId: string | undefined) {
  return useQuery({
    queryKey: ['projects', projectId],
    queryFn: async () => {
      if (!projectId) throw new Error('Project ID required');
      const response = await projectsApi.getById(projectId);
      return response.data as Project;
    },
    enabled: !!projectId,
  });
}

export function useProjectDashboard(projectId: string | undefined) {
  return useQuery({
    queryKey: ['projects', projectId, 'dashboard'],
    queryFn: async () => {
      if (!projectId) throw new Error('Project ID required');
      const response = await projectsApi.getDashboard(projectId);
      return response.data as ProjectDashboard;
    },
    enabled: !!projectId,
  });
}

export function useCreateProject() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: { name: string; description?: string; color?: string }) => {
      const response = await projectsApi.create(data);
      return response.data as Project;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['projects'] });
    },
  });
}

export function useUpdateProject() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      id,
      data,
    }: {
      id: string;
      data: { name?: string; description?: string; color?: string; icon?: string | null };
    }) => {
      const response = await projectsApi.update(id, data);
      return response.data as Project;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['projects'] });
      queryClient.invalidateQueries({ queryKey: ['projects', variables.id] });
    },
  });
}

export function useArchiveProject() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      const res = await projectsApi.archive(id);
      return res.data as Project;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['projects'] });
    },
  });
}

export function useUnarchiveProject() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      const res = await projectsApi.unarchive(id);
      return res.data as Project;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['projects'] });
    },
  });
}

export function useDeleteProject() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      await projectsApi.delete(id);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['projects'] });
    },
  });
}

// ============================================
// PROJECT STATUS MANAGEMENT HOOKS
// ============================================

export function useProjectStatuses(projectId: string | undefined) {
  return useQuery({
    queryKey: ['projectStatuses', projectId],
    queryFn: async () => {
      if (!projectId) return [];
      const response = await projectsApi.getStatuses(projectId);
      return response.data as TaskStatus[];
    },
    enabled: !!projectId,
  });
}

export function useCreateProjectStatus() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      projectId,
      name,
      color,
      isDoneState,
    }: {
      projectId: string;
      name: string;
      color?: string;
      isDoneState?: boolean;
    }) => {
      const response = await projectsApi.createStatus(projectId, { name, color, isDoneState });
      return response.data as TaskStatus;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['projectStatuses', variables.projectId] });
      queryClient.invalidateQueries({ queryKey: ['projects', variables.projectId] });
    },
  });
}

export function useUpdateProjectStatus() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      projectId,
      statusId,
      name,
      color,
      isDoneState,
    }: {
      projectId: string;
      statusId: string;
      name?: string;
      color?: string;
      isDoneState?: boolean;
    }) => {
      const response = await projectsApi.updateStatus(projectId, statusId, { name, color, isDoneState });
      return response.data as TaskStatus;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['projectStatuses', variables.projectId] });
      queryClient.invalidateQueries({ queryKey: ['projects', variables.projectId] });
    },
  });
}

export function useDeleteProjectStatus() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      projectId,
      statusId,
      replacementStatusId,
    }: {
      projectId: string;
      statusId: string;
      replacementStatusId?: string;
    }) => {
      await projectsApi.deleteStatus(projectId, statusId, replacementStatusId);
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['projectStatuses', variables.projectId] });
      queryClient.invalidateQueries({ queryKey: ['projects', variables.projectId] });
      queryClient.invalidateQueries({ queryKey: ['tasks', variables.projectId] });
    },
  });
}

export function useReorderProjectStatuses() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      projectId,
      statusIds,
    }: {
      projectId: string;
      statusIds: string[];
    }) => {
      const response = await projectsApi.reorderStatuses(projectId, statusIds);
      return response.data as TaskStatus[];
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['projectStatuses', variables.projectId] });
      queryClient.invalidateQueries({ queryKey: ['projects', variables.projectId] });
    },
  });
}

// ============================================
// PROJECT MEMBER MANAGEMENT HOOKS
// ============================================

export function useProjectMembers(projectId: string | undefined) {
  return useQuery({
    queryKey: ['projects', projectId, 'members'],
    queryFn: async () => {
      if (!projectId) throw new Error('Project ID required');
      const response = await projectsApi.getMembers(projectId);
      return response.data;
    },
    enabled: !!projectId,
  });
}

export function useAddProjectMember() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      projectId,
      userId,
      role,
    }: {
      projectId: string;
      userId: string;
      role?: string;
    }) => {
      const response = await projectsApi.addMember(projectId, { userId, role });
      return response.data;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['projects', variables.projectId, 'members'] });
      queryClient.invalidateQueries({ queryKey: ['projects', variables.projectId] });
    },
  });
}

export function useRemoveProjectMember() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      projectId,
      memberId,
    }: {
      projectId: string;
      memberId: string;
    }) => {
      await projectsApi.removeMember(projectId, memberId);
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['projects', variables.projectId, 'members'] });
      queryClient.invalidateQueries({ queryKey: ['projects', variables.projectId] });
    },
  });
}
